import type { PublicUser } from '@sbt-web/auth';
import {
  Category,
  getCategoryByID,
  getMacroOfCategoryID,
  isLeafCategory,
} from '@sbt-web/category-utils';
import {
  AdItem,
  AdTypeKeyValue,
  AdTypes,
  FilterURI,
  GeoEntry,
  ItemAdvertiser,
  ItemCategory,
  ItemFeatures,
} from '@sbt-web/network/types';
import { RadiusSearch } from '@sbt-web/search-autocomplete';
import { sendInitEventToGTMNextPaint } from '@sbt-web/tracking';
import {
  markPerformance,
  Marks,
} from '@/client/utilities/performance/performanceMonitor';
import type { FiltersConfigState } from '@/reducers/filtersConfig';
import { getStore, type RootState } from '@/reducers/index';
import type { SearchState } from '@/reducers/search';
import { mapPackToItemFeatures } from '@/shared/helpers/PackFeatures';
import { extractAdItems, type DecoratedItem } from '@/shared/models';
import {
  adTypesLabelsMap,
  ordersFullLabelsMap,
  QSOLabel,
  shippableOnlyLabel,
  urgentLabel,
} from '@/tools/search/values';
import { onDataLayerReady } from '@/tools/tracking/utils';
import { getUrnRank } from './navigation-data';

interface DLBaseContent {
  id: string;
  name: string;
}

export enum DLsubscription {
  PRO = 'pro',
  FREEMIUM = 'freemium',
}

export interface DLAdvertiser {
  id: string;
  type: string;
  shop_id: string;
  subscription: DLsubscription;
}

interface DLCategory {
  child: DLBaseContent;
  parent: DLBaseContent;
}

interface DLGeo {
  region?: DLBaseContent;
  city?: DLBaseContent;
  town?: DLBaseContent;
}

export interface DLAdType {
  id: string;
  name: string;
  weight?: number;
}

interface DLAd {
  urn: string;
  type: DLAdType | null;
  subject: string;
  geo: DLGeo;
  advertiser: DLAdvertiser;
  category: DLCategory;
  features: { [key: string]: DLBaseContent };
  rank?: number | null;
}

interface DLSearch {
  id?: string;
  category: DLCategory;
  adType: DLBaseContent;
  filters: { [key: string]: DLBaseContent };
  geo: DLGeo | null;
  query: string;
  sort: DLBaseContent;
  page: number;
  radiusSearch?: RadiusSearch;
}

export interface DataLayer {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  push(data: any): void;
  clearAds(): void;
  subscribe(modelName: string, cb: () => void): void;
  unsubscribe(modelName: string, cb: () => void): void;
  ads: {
    totalResults: string;
  } & DLAd[];
  search: DLSearch;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const getDLItemCategory = (cat: ItemCategory): DLCategory => {
  const macro = getCategoryByID(cat.parentId);
  return {
    child: {
      id: cat.id,
      name: cat.label,
    },
    parent: {
      id: macro.id,
      name: macro.label,
    },
  };
};

const getDLFeatures = (
  features: ItemFeatures
): Record<string, DLBaseContent> => {
  const featuresWithPack: ItemFeatures = mapPackToItemFeatures(features);

  return Object.entries(featuresWithPack).reduce(
    (acc, [key, feat]) => {
      const newFeat: DLBaseContent = {
        id: feat.values[0].key,
        name: feat.values[0].value,
      };
      acc[key] = newFeat;
      return acc;
    },
    {} as Record<string, DLBaseContent>
  );
};

const getDLGeo = (geo: GeoEntry): DLGeo => {
  const dlGeo: DLGeo = {
    region: { id: geo.region.id, name: geo.region.value },
  };
  if (geo.city) {
    dlGeo.city = { id: geo.city?.id, name: geo.city?.value };
  }
  if (geo.town) {
    dlGeo.town = { id: geo.town?.id, name: geo.town?.value };
  }
  return dlGeo;
};

const getDLAdType = (type: AdTypeKeyValue | null): DLAdType | null => {
  if (!type) {
    return null;
  }

  const retType = {
    id: type.key,
    name: type.value,
  };

  if (type.weight) {
    return {
      ...retType,
      weight: type.weight,
    };
  }

  return retType;
};

const getDLadvertiser = (advertiser: ItemAdvertiser): DLAdvertiser => {
  const subscription =
    advertiser.type === 0 ? DLsubscription.FREEMIUM : DLsubscription.PRO;

  return {
    id: advertiser.userId,
    type: advertiser.type.toString(),
    shop_id: advertiser.shopId || '',
    subscription,
  };
};

const getDLAds = (items: DecoratedItem[]): DLAd[] =>
  extractAdItems(items).map((item) => ({
    advertiser: getDLadvertiser(item.advertiser),
    type: getDLAdType(item.type),
    category: getDLItemCategory(item.category),
    features: getDLFeatures(item.features),
    geo: getDLGeo(item.geo),
    subject: item.subject,
    urn: item.urn,
  }));

const getDLdetailAd = (item: AdItem): DLAd => {
  const dlAd: DLAd = {
    urn: item.urn,
    type: getDLAdType(item.type),
    subject: item.subject,
    geo: getDLGeo(item.geo),
    advertiser: getDLadvertiser(item.advertiser),
    category: getDLItemCategory(item.category),
    features: getDLFeatures(item.features),
    rank: getUrnRank(item.urn),
  };

  return dlAd;
};

const getDLSearchCategory = (cat: Category): DLCategory => {
  const catData: DLBaseContent = { id: cat.id, name: cat.label };
  if (isLeafCategory(cat)) {
    const macro = getMacroOfCategoryID(cat.id);
    return {
      parent: { id: macro.id, name: macro.label },
      child: catData,
    };
  } else {
    return {
      parent: catData,
      child: catData,
    };
  }
};

const getDLSearchFilters = (
  search: SearchState,
  filtersConfig: FiltersConfigState
): { [key: string]: DLBaseContent } => {
  const DLFilters: { [key: string]: DLBaseContent } = {};
  if (search.qso) {
    DLFilters[FilterURI.QuerySubjectOnly] = {
      id: 'true',
      name: QSOLabel,
    };
  }
  if (search.includeShippableOnly) {
    DLFilters[FilterURI.ItemShippable] = {
      id: 'true',
      name: shippableOnlyLabel,
    };
  }
  if (search.includeUrgent) {
    DLFilters[FilterURI.ItemUrgent] = {
      id: 'true',
      name: urgentLabel,
    };
  }
  const config = filtersConfig.filtersStore;

  if (!config) {
    return DLFilters;
  } else {
    const filters = config.sparseFilters;
    return Object.keys(search.filters).reduce((acc, key) => {
      const found = filters.find((f) => f.queryString === key);
      const foundLabel = key in search.filtersLabels;
      if (found && foundLabel) {
        acc[found.uri] = {
          id: search.filters[key],
          name: search.filtersLabels[key],
        };
      }
      return acc;
    }, DLFilters);
  }
};

class DataLayerBridge {
  #previousState: RootState | undefined;

  #page: 'listing' | 'adview';
  /**
   * The item displayed on the detail page.
   * It is no longer in the state, but I'm trying to make minimal changes.
   * On listing pages, this is null.
   */
  #detailItem: AdItem | null = null;

  constructor(page: 'listing' | 'adview', detailItem: AdItem | null = null) {
    this.#page = page;

    if (page === 'adview' && detailItem) {
      this.#detailItem = detailItem;
    }
  }

  get isListingPage(): boolean {
    return this.#page === 'listing';
  }

  get isAdDetailPage(): boolean {
    return this.#page === 'adview';
  }

  static #isSearchStateUpdated = (
    previousState: SearchState,
    state: SearchState
  ): boolean => {
    return (
      previousState !== state &&
      previousState.isUpdating === state.isUpdating &&
      previousState.filtersDialogOpen === state.filtersDialogOpen
    );
  };

  updateDataLayerAdsListing(state: RootState): void {
    if (
      (!this.#previousState ||
        this.#previousState.items.list !== state.items.list) &&
      window.subito.dataLayer
    ) {
      window.subito.dataLayer.clearAds?.();
      if (window.subito.dataLayer.ads) {
        window.subito.dataLayer.ads.totalResults = state.items.total.toString();
      }
      window.subito.dataLayer.push({
        ads: getDLAds(state.items.list),
      });
    }
  }

  updateDataLayerAds(state: RootState): void {
    if (this.isListingPage) {
      this.updateDataLayerAdsListing(state);
    }
  }

  updateDataLayerSearch(state: RootState): void {
    if (
      this.isListingPage &&
      (!this.#previousState ||
        DataLayerBridge.#isSearchStateUpdated(
          this.#previousState.search,
          state.search
        ))
    ) {
      const search: DLSearch = {
        id: state.search.id ?? '',
        category: getDLSearchCategory(state.search.category),
        adType: {
          id: state.search.adType
            ? state.search.adType.toString()
            : AdTypes.Sell,
          name:
            adTypesLabelsMap[state.search.adType] ||
            adTypesLabelsMap[AdTypes.Sell],
        },
        filters: getDLSearchFilters(state.search, state.filtersConfig),
        geo: getDLGeo(state.search.geo),
        query: state.search.query ?? '',
        sort: {
          id: state.search.category.orders[state.search.orderIndex],
          name: ordersFullLabelsMap[
            state.search.category.orders[state.search.orderIndex]
          ],
        },
        page: state.search.page,
      };

      window.subito.dataLayer?.push({ search });
    }
  }

  update(state: RootState): void {
    this.updateDataLayerSearch(state);
    this.updateDataLayerAds(state);
    this.#previousState = state;
    markPerformance(Marks.DATA_LAYER_IS_READY);
    onDataLayerReady();
  }

  /**
   * Prepares the data layer with the static information of the page and
   * environment, updates the data layer with the current state and subscribes
   * to the store to enable cascading updates.
   */
  register(user: PublicUser | null): void {
    // One-time setup of the Ad Detail layer
    if (this.#page === 'adview' && window.subito.dataLayer) {
      window.subito.dataLayer.clearAds();
      window.subito.dataLayer.ads.totalResults = 1;

      if (this.#detailItem !== null) {
        window.subito.dataLayer.push({
          ads: [getDLdetailAd(this.#detailItem)],
        });
      }
    }

    if (user !== null) {
      window.subito.dataLayer?.push({
        user: {
          id: user.id,
          email: user.email,
        },
      });
    }

    window.subito.dataLayer?.push({
      env: {
        name: process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT,
        cachebuster: '',
        tracking: {},
        application: 'orodha',
      },
      page: {
        name: document.title,
        page_type: this.#page,
      },
    });

    const store = getStore(undefined, false);

    const updateDataLayer = (): void => this.update(store.getState());

    // Run the first update manually and then update on every redux state change
    updateDataLayer();
    // This will run on search and item updates on the listing, and user updates on both pages
    store.subscribe(updateDataLayer);

    sendInitEventToGTMNextPaint();
  }
}

export { DataLayerBridge, getDLAdType, getDLadvertiser };
export type { DLAd, DLCategory, DLGeo };
